// Beta Testers — ad-hoc iOS beta portal monitor. Read-only view of who has
// enrolled in the self-hosted beta portal (enroll → UDID → EAS ad-hoc build →
// install), their state, and the shared 100-device Apple slot usage.
// super_admin / reseller only; data proxied via GET /api/beta-testers.
import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { FlaskConical, RefreshCw, Smartphone, Hammer } from 'lucide-react';
import { api } from '../lib/api';
import type { BetaTester } from '../lib/api';

const STATE_BADGES: Record<string, string> = {
  ready:      'bg-green-500/20 text-green-400',
  registered: 'bg-blue-500/20 text-blue-400',
  created:    'bg-slate-500/20 text-slate-400',
  building:   'bg-orange-500/20 text-orange-400',
};

function stateLabel(s: string) {
  if (s === 'created') return 'enrolling';
  return s;
}

export default function BetaTesters() {
  const me = api.me();
  const isAdmin = me?.role === 'super_admin' || me?.role === 'reseller';

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['beta-testers'],
    queryFn: () => api.betaTesters.status(),
    enabled: isAdmin,
    refetchInterval: 30000,
  });

  if (!isAdmin) return <Navigate to="/apps" replace />;

  const testers = data?.testers ?? [];
  const slots = data?.slots;
  const build = data?.latestBuild;
  const building = data?.worker?.building;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><FlaskConical /> Beta Testers</h1>
        <button className="btn-ghost inline-flex items-center gap-1 text-sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <p className="text-textSecondary text-sm">
        Ad-hoc iOS beta distribution (self-hosted). Testers enroll at{' '}
        <a href="https://beta.computers2go.cloud/enroll" target="_blank" rel="noreferrer" className="text-primary">
          beta.computers2go.cloud
        </a>
        , register their device UDID, and receive an install link once the build covering them finishes.
      </p>

      {isLoading && <div className="card p-6 text-textSecondary">Loading…</div>}
      {isError && (
        <div className="card p-6 text-textSecondary">
          Couldn't reach the beta portal. {(error as Error)?.message}
        </div>
      )}

      {!isLoading && !isError && (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="card p-4">
              <div className="text-xs text-textSecondary uppercase tracking-wide flex items-center gap-1"><Smartphone size={13} /> Apple device slots</div>
              <div className="text-2xl font-semibold mt-1">{slots ? `${slots.used} / ${slots.cap}` : '—'}</div>
              <div className="text-xs text-textSecondary">{slots ? `${slots.remaining} remaining this year` : ''}</div>
            </div>
            <div className="card p-4">
              <div className="text-xs text-textSecondary uppercase tracking-wide">Enrolled testers</div>
              <div className="text-2xl font-semibold mt-1">{testers.length}</div>
              <div className="text-xs text-textSecondary">{testers.filter(t => t.state === 'ready').length} ready to install</div>
            </div>
            <div className="card p-4">
              <div className="text-xs text-textSecondary uppercase tracking-wide flex items-center gap-1"><Hammer size={13} /> Latest build</div>
              <div className="text-2xl font-semibold mt-1">{build?.appVersion ? `v${build.appVersion}` : '—'}</div>
              <div className="text-xs text-textSecondary">
                {building ? 'building now…' : build?.finishedAt ? new Date(build.finishedAt).toLocaleString() : 'no builds yet'}
              </div>
            </div>
          </div>

          <div className="card p-0 overflow-hidden">
            <table className="table">
              <thead><tr>
                <th></th>
                <th>Email</th>
                <th>State</th>
                <th>Device UDID</th>
                <th>Enrolled</th>
              </tr></thead>
              <tbody>
                {testers.map((t: BetaTester) => (
                  <tr key={t.email + (t.udid || '')} className="hover:bg-elevated">
                    <td>
                      <span className={`inline-block w-2 h-2 rounded-full ${t.state === 'ready' ? 'bg-success' : t.udid ? 'bg-warning' : 'bg-danger'}`} />
                    </td>
                    <td>{t.email}</td>
                    <td><span className={`badge ${STATE_BADGES[t.state] || ''}`}>{stateLabel(t.state)}</span></td>
                    <td className="font-mono text-xs text-textSecondary">{t.udid || <span className="text-textSecondary">not registered</span>}</td>
                    <td className="text-xs">{t.createdAt ? new Date(t.createdAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
                {testers.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-textSecondary">No one has enrolled yet.</td></tr>}
              </tbody>
            </table>
          </div>

          <div className="card p-3 text-sm text-textSecondary">
            <strong>How it works:</strong> a new UDID is baked into the next ad-hoc build automatically
            (builds coalesce over a few minutes). When a build finishes, every covered tester is moved to
            <span className="text-textPrimary"> ready</span> and emailed an install link. The 100-slot cap
            is per Apple Developer membership year.
          </div>
        </>
      )}
    </div>
  );
}
